// CB:BEGIN admin-project-detail-page
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@clerk/clerk-react';
// CB:BEGIN priority2-project-management-import
import { ProjectManagementActions } from '../../components/admin/ProjectManagementActions';
// CB:END priority2-project-management-import
// CB:BEGIN help-support-ops-import
import HelpSupportOpsPanel from '../../components/help/HelpSupportOpsPanel';
// CB:END help-support-ops-import

interface Project {
  id: string;
  address: string;
  status: string;
  createdAt: string;
  preferredContractorId?: string | null;
  preferredContractorState?: string | null;
  preferredContractor?: {
    id: string;
    company: string | null;
  } | null;
  homeowner?: {
    user: {
      email: string;
    };
  };
  contractor?: {
    user: {
      email: string;
    };
  };
  bids: Array<{
    id: string;
    amountCents?: number;
    isAccepted: boolean;
    contractor?: {
      user: {
        email: string;
      };
    };
  }>;
}

export default function AdminProjectDetailPage() {
  const { id } = useParams();
  const { getToken } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [contractorId, setContractorId] = useState('');

  const loadProject = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await fetch(`/api/admin/projects/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setProject(data.project);
      setStatus(data.project.status);
    } catch (e: any) {
      console.error('Error loading project:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProject();
  }, [id, getToken]);

  const saveStatus = async () => {
    if (!id) return;
    
    try {
      const token = await getToken();
      
      const response = await fetch(`/api/admin/projects/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Reload project to reflect changes
      loadProject();
    } catch (e: any) {
      console.error('Error updating project status:', e);
      alert(`Error: ${e.message}`);
    }
  };

  const assignContractor = async () => {
    if (!id || !contractorId) return;
    
    try {
      const token = await getToken();
      
      const response = await fetch(`/api/admin/projects/${id}/assign-contractor`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ contractorId })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Reload project to reflect changes
      loadProject();
      setContractorId('');
    } catch (e: any) {
      console.error('Error assigning contractor:', e);
      alert(`Error: ${e.message}`);
    }
  };

  const acceptBid = async (bidId: string) => {
    if (!id) return;
    
    try {
      const token = await getToken();
      
      const response = await fetch(`/api/admin/projects/${id}/accept-bid`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ bidId })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Reload project to reflect changes
      loadProject();
    } catch (e: any) {
      console.error('Error accepting bid:', e);
      alert(`Error: ${e.message}`);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Project Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading project...</span>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Project Details">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600 text-sm">Error loading project: {error}</p>
          <button 
            onClick={loadProject}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  if (!project) {
    return (
      <AdminLayout title="Project Details">
        <div className="text-center py-12">
          <p className="text-gray-500">Project not found</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`Project: ${project.address}`}>
      <div className="space-y-6">
        {/* Project Info */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Project Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <p className="mt-1 text-sm text-gray-900">{project.address}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Created</label>
              <p className="mt-1 text-sm text-gray-900">{new Date(project.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Homeowner</label>
              <p className="mt-1 text-sm text-gray-900">
                {project.homeowner?.user?.email || 'No homeowner assigned'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Contractor</label>
              <p className="mt-1 text-sm text-gray-900">
                {project.contractor?.user?.email || 'No contractor assigned'}
              </p>
            </div>
            {project.preferredContractorId && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Preferred Contractor</label>
                <p className="mt-1 text-sm text-gray-900">
                  {project.preferredContractor?.company || project.preferredContractorId}
                </p>
              </div>
            )}
            {project.preferredContractorState && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Preferred State</label>
                <p className="mt-1 text-sm text-gray-900">{project.preferredContractorState}</p>
              </div>
            )}
          </div>
        </div>

        {/* CB:BEGIN help-support-ops-panel */}
        <HelpSupportOpsPanel
          defaultRole="homeowner"
          contextLabel={`Project ${project.id}`}
          recipientName={project.homeowner?.user?.email || 'there'}
        />
        {/* CB:END help-support-ops-panel */}

        {/* CB:BEGIN priority2-project-management-component */}
        {/* Project Management Actions */}
        <ProjectManagementActions
          projectId={project.id}
          projectAddress={project.address}
          currentStatus={project.status}
          onSuccess={loadProject}
        />
        {/* CB:END priority2-project-management-component */}

        {/* Status Management */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Status Management</h2>
          <div className="flex items-center gap-4">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
            </select>
            <button
              onClick={saveStatus}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Update Status
            </button>
          </div>
        </div>

        {/* Contractor Assignment */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Assign Contractor</h2>
          <div className="flex items-center gap-4">
            <input
              type="text"
              value={contractorId}
              onChange={(e) => setContractorId(e.target.value)}
              placeholder="Enter contractor ID..."
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={assignContractor}
              disabled={!contractorId}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Assign Contractor
            </button>
          </div>
        </div>

        {/* Bids Management */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Bids</h2>
          {project.bids.length > 0 ? (
            <div className="space-y-3">
              {project.bids.map((bid) => (
                <div key={bid.id} className="flex items-center justify-between border rounded-lg p-4">
                  <div>
                    <div className="font-medium">
                      ${bid.amountCents ? (bid.amountCents / 100).toFixed(2) : 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">
                      Contractor: {bid.contractor?.user?.email || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-400">
                      Status: {bid.isAccepted ? 'ACCEPTED' : 'PENDING'}
                    </div>
                  </div>
                  {!bid.isAccepted && (
                    <button
                      onClick={() => acceptBid(bid.id)}
                      className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                    >
                      Accept Bid
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No bids received</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
// CB:END admin-project-detail-page
